import { useEffect, useState } from 'react';

import { api } from '../api.js';
import { useAuth } from '../auth.jsx';
import { useT } from '../lib/i18n.js';
import { accountTheme, applyTheme, readTheme, storeTheme, THEME_ENDPOINTS, THEMES, themeOwner } from '../lib/theme.js';

function Svg({ children }) {
  return (
    <svg
      viewBox="0 0 16 16"
      className="size-[1em]"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      {children}
    </svg>
  );
}

const ICONS = {
  system: (
    <Svg>
      <rect x="1.8" y="2.5" width="12.4" height="8.5" rx="1.2" />
      <path d="M5.5 13.5h5M8 11v2.5" />
    </Svg>
  ),
  light: (
    <Svg>
      <circle cx="8" cy="8" r="2.8" />
      <path d="M8 1.5v1.3M8 13.2v1.3M1.5 8h1.3M13.2 8h1.3M3.4 3.4l.9.9M11.7 11.7l.9.9M3.4 12.6l.9-.9M11.7 4.3l.9-.9" />
    </Svg>
  ),
  dark: (
    <Svg>
      <path d="M13.5 9.6A5.6 5.6 0 0 1 6.4 2.5a5.6 5.6 0 1 0 7.1 7.1Z" />
    </Svg>
  ),
};

/**
 * Light / dark / system, as three radio buttons (arrow keys move between them). The choice applies
 * at once, stays in this browser and, for an account, is saved on it (lib/theme.js), so it follows
 * the person to other devices. `realm` is the side of the app: 'client' or 'staff'. `tone="ink"` for
 * the dark sidebar.
 */
export function ThemePicker({ realm = 'client', tone = 'page', className = '' }) {
  const t = useT();
  const { me, refresh } = useAuth() ?? {};
  const fromAccount = accountTheme(me, realm);
  const [theme, setTheme] = useState(() => fromAccount ?? readTheme());

  // Signing in (or the account loading) brings the account's choice to this browser.
  useEffect(() => {
    if (!fromAccount) return;
    setTheme(fromAccount);
    applyTheme(fromAccount);
    storeTheme(fromAccount);
  }, [fromAccount]);

  async function choose(value) {
    setTheme(value);
    applyTheme(value);
    storeTheme(value);
    const owner = themeOwner(me, realm);
    if (!owner) return;
    try {
      await api(THEME_ENDPOINTS[owner], { method: 'PATCH', body: { theme: value } });
      refresh?.();
    } catch {
      // Kept for this browser; the account keeps its old choice until the next try.
    }
  }

  const ink = tone === 'ink';
  return (
    <fieldset className={`flex items-center gap-2 ${className}`}>
      <legend className="sr-only">{t('theme.label')}</legend>
      <span aria-hidden="true" className={ink ? 'text-xs text-white/60' : ''}>
        {t('theme.label')}
      </span>
      <span className={`inline-flex rounded-md border p-0.5 ${ink ? 'border-white/15' : 'border-line bg-surface'}`}>
        {THEMES.map((value) => {
          const on = theme === value;
          return (
            <label
              key={value}
              title={t(`theme.${value}`)}
              className={`flex cursor-pointer items-center rounded px-1.5 py-1 has-focus-visible:outline-2 has-focus-visible:outline-offset-1 has-focus-visible:outline-(--focus) ${
                on
                  ? ink
                    ? 'bg-white/15 text-white'
                    : 'bg-accent text-on-accent'
                  : ink
                    ? 'text-white/60 hover:text-white'
                    : 'text-muted hover:text-text'
              }`}
            >
              <input
                type="radio"
                name={`theme-${realm}`}
                value={value}
                checked={on}
                onChange={() => choose(value)}
                className="sr-only"
              />
              {ICONS[value]}
              <span className="sr-only">{t(`theme.${value}`)}</span>
            </label>
          );
        })}
      </span>
    </fieldset>
  );
}
